import { Bot, CalendarClock, Workflow, MessagesSquare, Wrench, X, BrainCircuit, Settings, HeartPulse, Terminal, MessageSquare, Mail, CreditCard, LayoutGrid, SlidersHorizontal, Puzzle } from "lucide-react";
import { SuperAdminOrgSwitcher } from "./SuperAdminOrgSwitcher";
import { OrgSwitcherHint } from "./OrgSwitcherHint";
import { cn } from "@/infrastructure/lib/utils";
import { Button } from "@/shared/components/ui/button";
import { ScrollArea } from "@/shared/components/ui/scroll-area";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import { useOrganization, useOrganizationDetails } from "@/shared/hooks/api/useOrganization";
import { Separator } from "@/shared/components/ui/separator";
import { useIsAdmin } from "@/shared/hooks/use-admin-validation";
import { useIsSuperAdminRole } from "@/shared/hooks/use-system-role";
import { useSuperAdminOrg } from "@/shared/contexts/SuperAdminOrgContext";
import { useSidebarSubscription } from "@/shared/hooks/api/useSidebarSubscription";
import { useAuth } from "@/features/Auth/state/AuthContext";
import { useQueryClient } from "@tanstack/react-query";
import { useViewScope, type ViewScope } from "@/shared/hooks/use-view-scope";
import { useWorkspace } from "@/shared/hooks/api/useWorkspace";
import { useChatActivity } from "@/features/Playground/hooks/useChatActivity";
import { BackgroundTasksMarker } from "@/features/Playground/components/BackgroundTasksIndicator";
import { useEffect, useState, useRef, useMemo, type ComponentType } from "react";

const FALLBACK_ORG = {
  name: "Organização",
  logo_url: ""
};

interface SidebarProps {
  /** Gaveta aberta (celular). No desktop a barra não some. */
  isOpen: boolean;
  /** Desktop: recolhida em ícones. */
  collapsed?: boolean;
  /** Mostra os nomes agora — completa no desktop ou gaveta aberta. */
  expanded?: boolean;
  onClose: () => void;
  /** Volta a barra recolhida para completa (o avatar da organização). */
  onExpand?: () => void;
}

interface MenuItem {
  icon: ComponentType<{ className?: string }>;
  label: string;
  href: string;
  scopes: ViewScope[];
  tourId?: string;
}

export const Sidebar = ({ isOpen, collapsed = false, expanded, onClose, onExpand }: SidebarProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated } = useAuth();
  const queryClient = useQueryClient();
  const { data: userProfile, isLoading: isLoadingProfile, refetch: refetchProfile } = useOrganization();
  const { data: organizationDetails, refetch: refetchOrgDetails } = useOrganizationDetails(userProfile?.organization_id);

  const { isAdmin } = useIsAdmin();
  const { isSuperAdmin } = useIsSuperAdminRole();
  const { selectedOrganization, organizations } = useSuperAdminOrg();
  const { planName, subStatus, statusLabel, statusColor } = useSidebarSubscription();
  const { scope } = useViewScope();
  const isUserOnly = !isAdmin && !isSuperAdmin;
  // A bolinha do Chat (AB#86889): conversas do workspace com o agente respondendo ou tarefa em
  // segundo plano. Só para quem está fora do Chat — lá dentro, a conversa já mostra o andamento.
  const { currentWorkspace } = useWorkspace();
  const onChat = location.pathname === "/playground";
  const activeChats = useChatActivity(currentWorkspace?.id, scope === 'product' && !onChat);

  // O card vira seletor quando há para onde ir (AB#84509): superadmin navega a
  // plataforma inteira, e qualquer usuário com mais de um vínculo alterna entre as
  // suas. Convite pendente NÃO abre o seletor — ele vive no sino de notificações.
  const canSwitchOrganization = isSuperAdmin || organizations.length > 1;
  const [orgName, setOrgName] = useState<string>("");
  const [orgLogo, setOrgLogo] = useState<string>("");
  const [logoUpdateCounter, setLogoUpdateCounter] = useState<number>(0);
  const dataLoadedRef = useRef<boolean>(false);
  const retryTimeoutRef = useRef<NodeJS.Timeout>();

  useEffect(() => {
    if (!isAuthenticated || !user || userProfile || !isLoadingProfile) {
      return;
    }

    let retryCount = 0;
    const MAX_RETRIES = 3;

    const attemptProfileFetch = async () => {
      retryCount++;

      try {
        queryClient.removeQueries({ queryKey: ['current-user-profile'] });
        const result = await refetchProfile();

        if (result.data) {
          return;
        }
      } catch (error) {
        console.warn(`[Sidebar] Tentativa ${retryCount}/${MAX_RETRIES} falhou:`, error);
      }

      if (retryCount < MAX_RETRIES) {
        retryTimeoutRef.current = setTimeout(attemptProfileFetch, 500 * retryCount);
      }
    };

    retryTimeoutRef.current = setTimeout(attemptProfileFetch, 300);

    return () => {
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
    };
  }, [isAuthenticated, user, userProfile, isLoadingProfile, queryClient, refetchProfile]);

  useEffect(() => {
    if (userProfile?.organization_id && !organizationDetails) {
      refetchOrgDetails();
    }
  }, [userProfile, organizationDetails, refetchOrgDetails]);

  // O card exibe a organização ATIVA, para qualquer papel (AB#84509). Antes o
  // caminho não-superadmin lia a organização do perfil e só uma vez — com
  // multi-organização isso deixaria o card preso no nome da organização de origem
  // depois de trocar.
  useEffect(() => {
    if (selectedOrganization) {
      setOrgName(selectedOrganization.name || "");
      setOrgLogo(selectedOrganization.logo_url || "");
      setLogoUpdateCounter(prev => prev + 1);
      dataLoadedRef.current = true;
      return;
    }
    // Superadmin em escopo de plataforma não tem organização — o card fica vazio de
    // propósito. Os demais caem aqui só enquanto a lista não resolveu.
    if (isSuperAdmin) {
      setOrgName("");
      setOrgLogo("");
      return;
    }
    if (organizationDetails && !dataLoadedRef.current) {
      setOrgName(organizationDetails.name || "");
      setOrgLogo(organizationDetails.logo_url || "");
      dataLoadedRef.current = true;
    }
  }, [selectedOrganization, organizationDetails, isSuperAdmin]);

  const logoUrlWithCacheBusting = orgLogo ? `${orgLogo}${orgLogo.includes('?') ? '&' : '?'}v=${logoUpdateCounter}` : '';

  // Reflete edição da organização (nome/logo) sem recarregar a página. Só vale
  // quando os detalhes carregados SÃO os da organização ativa: eles vêm da
  // organização do perfil, então após uma troca escreveriam o nome errado no card.
  useEffect(() => {
    if (isSuperAdmin) return;
    if (!organizationDetails || !dataLoadedRef.current) return;
    if (selectedOrganization && selectedOrganization.id !== organizationDetails.id) return;

    setOrgName(organizationDetails.name || "");
    setOrgLogo(organizationDetails.logo_url || "");
    setLogoUpdateCounter(prev => prev + 1);
  }, [organizationDetails, isSuperAdmin, selectedOrganization]);

  useEffect(() => {
    const interval = setInterval(() => {
      setLogoUpdateCounter(prev => prev + 1);
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  const menuItems: MenuItem[] = useMemo(() => [
    // Pulse: o sinal vital do workspace — os dashboards, no corpo que o menu conta (Brain, Skills, Jobs).
    { icon: HeartPulse, label: t("navigation.dashboard"), href: "/", scopes: ['platform', 'organization', 'product'], tourId: "sidebar-dashboards" },
    { icon: BrainCircuit, label: t("navigation.knowledge"), href: "/knowledge", scopes: ['product'] },
    { icon: Wrench, label: t("navigation.integrations"), href: "/integrations", scopes: ['organization', 'product'] },
    // Organização TAMBÉM (AB#85634, AC19): skill instalada no nível da
    // organização vale em todos os workspaces, e precisava de um lugar de onde
    // ser administrada. A tela é a mesma; o nível sai de `useViewScope`.
    // Visão pessoal TAMBÉM (AB#85637): o espaço de cada pessoa é o terceiro
    // nível, e era o único que não se alcançava pelo seletor. A tela é a
    // mesma; o nível sai de `useViewScope`.
    { icon: Puzzle, label: t("navigation.skills"), href: "/skills", scopes: ['organization', 'product', 'user'] },
    // Na organização e no My view o item é o Brain, como no workspace: dentro, a Memory
    // (AB#87149) e, no My view, os Learnings (AB#87286). O do workspace é o item de cima.
    { icon: BrainCircuit, label: t("navigation.knowledge"), href: "/memory", scopes: ['organization', 'user'] },
    // Depois das Skills (AB#86605): jobs e flows rodam Skills, então quem chega
    // aqui já passou pelo que eles executam. Jobs antes: é o caso mais simples.
    { icon: CalendarClock, label: t("navigation.jobs"), href: "/jobs", scopes: ['product'] },
    { icon: Workflow, label: t("navigation.agents"), href: "/workflows", scopes: ['product'] },
    { icon: MessagesSquare, label: t("navigation.playground"), href: "/playground", scopes: ['product'] },
    { icon: Terminal, label: t("settings.tabs.logs"), href: "/admin/logs", scopes: ['platform'] },
    { icon: Bot, label: t("settings.tabs.queues", "Queues"), href: "/admin/queues", scopes: ['platform'] },
    { icon: LayoutGrid, label: t("settings.tabs.templates", "Templates"), href: "/admin/templates", scopes: ['platform'] },
    { icon: MessageSquare, label: t("settings.tabs.feedback"), href: "/admin/feedback", scopes: ['platform'] },
    { icon: Mail, label: t("settings.sidebar.email"), href: "/admin/email", scopes: ['platform'] },
    { icon: CreditCard, label: t("settings.tabs.plans", "Plans"), href: "/admin/plans", scopes: ['platform'] },
    { icon: SlidersHorizontal, label: t("settings.tabs.platform"), href: "/admin/platform", scopes: ['platform'] },
  ], [t]);

  const visibleMenuItems = useMemo(() => {
    // user role: só vê menu quando há produto selecionado (sem visão da
    // organização) — ou na visão pessoal, que é o espaço dela e não depende de
    // papel nenhum.
    if (isUserOnly && scope !== 'product' && scope !== 'user') return [];
    return menuItems.filter(item => {
      if (!item.scopes.includes(scope)) return false;
      // items de platform scope são exclusivos de superadmin (evita flash em login de admin/user
      // antes do organization_id chegar do useOrganization)
      if (scope === 'platform' && !isSuperAdmin) return false;
      return true;
    });
  }, [menuItems, scope, isUserOnly, isSuperAdmin]);

  const handleNavigate = (href: string) => {
    navigate(href);
    if (window.innerWidth < 1024) {
      onClose();
    }
  };

  // Show org card whenever we have data to show OR we are superadmin (empty state handled by switcher)
  const showOrgCard = isSuperAdmin || (!!orgName && dataLoadedRef.current);

  return (
    <>
      <div
        className={cn(
          "fixed inset-0 bg-black/50 z-30 lg:hidden transition-opacity duration-300",
          isOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        )}
        onClick={onClose}
      />

      <aside
        className={cn(
          "fixed left-0 top-16 bottom-0 z-40 glass-effect w-[280px] transition-[transform,width] duration-300 flex flex-col lg:translate-x-0",
          collapsed ? "lg:w-16" : "lg:w-64",
          !isOpen && "-translate-x-full"
        )}
      >
        <button
          onClick={onClose}
          className="absolute right-4 top-4 p-2 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 lg:hidden"
        >
          <X className="h-4 w-4" />
        </button>

        <ScrollArea className="flex-grow py-6 relative">
          <div className="space-y-4 py-4">
            <div className={cn("px-3 py-2", collapsed && "lg:px-2")}>
              <div className="space-y-1">
                {visibleMenuItems.map((item) => {
                  const isActive = location.pathname === item.href;

                  return (
                    <Button
                      key={item.href}
                      // Sempre `ghost`: o item ativo indica ONDE VOCÊ ESTÁ, não
                      // uma ação. Usar a variante primária aqui gastava o token
                      // de maior ênfase do sistema em navegação e deixava o item
                      // do menu com mais peso visual que os CTAs de verdade.
                      variant="ghost"
                      title={collapsed ? item.label : undefined}
                      aria-label={item.label}
                      className={cn(
                        "relative w-full justify-start font-normal",
                        collapsed && "lg:justify-center lg:px-0",
                        // Barra da marca à esquerda: o azul entra como marcador,
                        // em área mínima, em vez de preencher o item.
                        isActive &&
                          "bg-active text-foreground before:absolute before:left-0 before:top-1 before:bottom-1 before:w-[3px] before:rounded-full before:bg-primary hover:bg-active"
                      )}
                      onClick={() => handleNavigate(item.href)}
                    >
                      <item.icon className={cn("mr-2 h-4 w-4 shrink-0", collapsed && "lg:mr-0")} />
                      <span className={cn("truncate", collapsed && "lg:hidden")}>{item.label}</span>
                      {item.href === "/playground" && (
                        <BackgroundTasksMarker
                          count={activeChats}
                          className={cn("ml-auto", collapsed && "lg:absolute lg:right-2 lg:top-2 lg:ml-0")}
                        />
                      )}
                    </Button>
                  );
                })}
              </div>
            </div>
          </div>
        </ScrollArea>

        {collapsed && showOrgCard && (
          // Recolhida, a organização vira um avatar com as configurações acima.
          // Quem troca de organização abre daqui a mesma lista, com busca, da
          // barra completa — nada some por a barra estar recolhida.
          <div className="hidden flex-col items-center gap-2 pb-4 lg:flex">
            <Separator className="mb-2 w-8" />
            {canSwitchOrganization ? (
              <SuperAdminOrgSwitcher
                compact
                orgName={orgName}
                orgLogo={orgLogo}
                logoUrl={logoUrlWithCacheBusting}
                planName={planName}
                subStatus={subStatus}
                statusLabel={statusLabel}
                statusColor={statusColor}
                onLogoError={() => setOrgLogo(FALLBACK_ORG.logo_url || "")}
                onNavigateSettings={() => handleNavigate("/settings")}
              />
            ) : (
              <>
                {isAdmin && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 rounded-full"
                    onClick={() => handleNavigate("/settings")}
                    title={t("navigation.settings")}
                    aria-label={t("navigation.settings")}
                  >
                    <Settings className="h-4 w-4" />
                  </Button>
                )}
                <button
                  type="button"
                  onClick={onExpand}
                  title={orgName}
                  aria-label={orgName}
                  className="h-10 w-10 overflow-hidden rounded-lg shadow-sm ring-offset-background transition hover:ring-2 hover:ring-primary/40"
                >
                  {orgLogo ? (
                    <img src={logoUrlWithCacheBusting} alt="" className="h-full w-full bg-secondary object-contain p-1" />
                  ) : (
                    <span className="flex h-full w-full items-center justify-center bg-primary text-sm text-primary-foreground">
                      {orgName?.[0]?.toUpperCase() || "?"}
                    </span>
                  )}
                </button>
              </>
            )}
          </div>
        )}

        <div className={cn("px-4 pb-4", collapsed && "lg:hidden")}>
          <Separator className="mb-4" />
          {/* Âncora do aviso: envolve SÓ o card, para ele encostar no card e não no
              separador — o alvo do recado é o card. */}
          <div className="relative">
            <OrgSwitcherHint sidebarOpen={expanded ?? isOpen} />
            <div
              data-org-switcher-card
              className={cn(
              "relative rounded-r-xl shadow-sm bg-transparent transition-all duration-500 ease-in-out",
              "before:absolute before:left-0 before:top-0 before:h-full before:w-1 before:bg-black",
              showOrgCard
                ? "opacity-100 border border-border p-4 translate-y-0"
                : "opacity-0 invisible h-0 p-0 overflow-hidden border-0 translate-y-2 before:hidden"
            )}>
              {showOrgCard && canSwitchOrganization ? (
                <SuperAdminOrgSwitcher
                  orgName={orgName}
                  orgLogo={orgLogo}
                  logoUrl={logoUrlWithCacheBusting}
                  planName={planName}
                  subStatus={subStatus}
                  statusLabel={statusLabel}
                  statusColor={statusColor}
                  onLogoError={() => setOrgLogo(FALLBACK_ORG.logo_url || "")}
                  onNavigateSettings={() => handleNavigate("/settings")}
                />
              ) : showOrgCard ? (
                <div className="flex items-center gap-3 animate-fadeIn">
                  <div className="relative h-12 w-12 rounded-lg overflow-hidden shadow-sm">
                    {orgLogo ? (
                      <img
                        src={logoUrlWithCacheBusting}
                        alt={orgName}
                        className="object-contain w-full h-full bg-secondary p-1"
                        onError={() => {
                          setOrgLogo(FALLBACK_ORG.logo_url || "");
                        }}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-primary text-primary-foreground">
                        {orgName?.[0]?.toUpperCase() || '?'}
                      </div>
                    )}
                  </div>
                  <div className="flex-1 overflow-hidden">
                    <p className="text-sm font-medium truncate mb-0.5">
                      {orgName}
                    </p>
                    {planName && (
                      <p className="text-xs truncate">
                        <span className="font-medium text-foreground underline underline-offset-2">{planName}</span>
                        {subStatus && planName?.toLowerCase() !== subStatus && <span className={cn("ml-1.5", statusColor[subStatus] || "text-muted-foreground")}>· {statusLabel[subStatus] || subStatus}</span>}
                      </p>
                    )}
                  </div>
                  {isAdmin && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="rounded-full h-8 w-8 hover:bg-blue-100/50 dark:hover:bg-blue-900/50 transition-colors duration-200"
                      onClick={() => handleNavigate("/settings")}
                      title={t("navigation.settings")}
                    >
                      <Settings className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </aside>
    </>
  );
};
